import { useRef, useState } from 'react';
import { responseStages } from '../../../shared/config/site';
import { useScrollChoreography } from '../../../shared/hooks/useScrollChoreography';
import { motionQuery } from '../../../shared/lib/gsap';

const TOTAL = responseStages.length;

/**
 * The pinned chapter.
 *
 * Two rates run through one scroll range: the readout is scrubbed continuously
 * while the stage rail steps in six discrete jumps. The number is deliberately a
 * sequence counter, not a clock — no response time is claimed anywhere here.
 */
export function ResponseChapter() {
  const rootRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const readoutRef = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(0);

  useScrollChoreography(rootRef, ({ gsap }) => {
    const media = gsap.matchMedia();

    media.add(motionQuery.full, () => {
      const counter = { value: 0 };

      gsap.to(counter, {
        value: 100,
        ease: 'none',
        scrollTrigger: {
          trigger: rootRef.current,
          start: 'top top',
          end: '+=170%',
          pin: stageRef.current,
          pinSpacing: true,
          scrub: 0.4,
          onUpdate: (self) => setActive(Math.min(Math.floor(self.progress * TOTAL), TOTAL - 1)),
        },
        onUpdate: () => {
          if (readoutRef.current) {
            readoutRef.current.textContent = String(Math.round(counter.value)).padStart(3, '0');
          }
        },
      });
    });

    return () => media.revert();
  });

  return (
    <section ref={rootRef} id="response" aria-label="How a callout runs">
      <div
        ref={stageRef}
        // The dock is fixed over the bottom of every phone viewport, so the
        // stage has to clear it or the last rows of the rail sit under it.
        className="relative flex h-[100dvh] min-h-[34rem] w-full items-center overflow-hidden bg-[color:var(--ink-900)] pb-[calc(4.25rem+env(safe-area-inset-bottom))] pt-[var(--banner-h,3rem)] lg:pb-0"
      >
        {/* The stage numeral runs behind the copy at its own rate. */}
        <span
          aria-hidden="true"
          className="u-display pointer-events-none absolute -left-[0.08em] top-1/2 -translate-y-1/2 select-none text-[color:var(--ink-700)]"
          style={{ fontSize: 'clamp(11rem, 30vw, 26rem)', lineHeight: 0.72 }}
        >
          {String(active + 1).padStart(2, '0')}
        </span>

        <div className="u-shell-padded relative grid gap-7 sm:gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <p className="u-label mb-6 text-[color:var(--steel-500)]">
              Sequence
              <span className="mx-2 text-[color:var(--signal-500)]">/</span>
              <span ref={readoutRef} className="u-mono">
                000
              </span>
            </p>

            <h2 className="u-balance u-display max-w-[13ch] text-[clamp(1.75rem,3.6vw,3rem)] text-[color:var(--bone-50)]">
              What happens after the panel fires
            </h2>

            <p className="u-measure mt-6 text-[color:var(--steel-400)]">
              The order of a callout. No response time is promised here.
            </p>
          </div>

          <ol className="flex flex-col">
            {responseStages.map((stage, index) => {
              const live = index === active;

              return (
                <li
                  key={stage.code}
                  className="u-rule grid grid-cols-[3.5rem_1fr] gap-4 py-3 transition-colors sm:py-4"
                  style={{
                    transitionDuration: 'var(--t-state)',
                    transitionTimingFunction: 'var(--e-settle)',
                    borderTopColor: live ? 'var(--signal-500)' : 'var(--color-border)',
                  }}
                >
                  <span
                    className="u-label pt-1 transition-colors"
                    style={{
                      transitionDuration: 'var(--t-state)',
                      transitionTimingFunction: 'var(--e-settle)',
                      color: live ? 'var(--signal-500)' : 'var(--steel-500)',
                    }}
                  >
                    {stage.code}
                  </span>
                  <span>
                    <span
                      className="u-display block text-[1.15rem] transition-colors sm:text-[1.4rem]"
                      style={{
                        transitionDuration: 'var(--t-state)',
                        transitionTimingFunction: 'var(--e-settle)',
                        color: live ? 'var(--bone-50)' : 'var(--steel-500)',
                      }}
                    >
                      {stage.label}
                    </span>
                    {/* Six notes plus six labels overran the pinned phone stage,
                        which is `overflow-hidden` — stages 05 and 06 could not be
                        read at all. The note follows the live row instead, the way
                        the hero chapters already do. The choice is made in CSS so
                        the prerendered markup and the hydrated markup agree. */}
                    <span
                      className={`mt-1 text-base transition-colors ${live ? 'block' : 'hidden sm:block'}`}
                      style={{
                        transitionDuration: 'var(--t-state)',
                        transitionTimingFunction: 'var(--e-settle)',
                        color: live ? 'var(--steel-400)' : 'var(--steel-500)',
                      }}
                    >
                      {stage.note}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
